import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import firebase from 'firebase/compat/app';
import 'firebase/compat/analytics'; // IMPORTANT: This MUST come after the firebase import but SHIFT-ALT-O breaks this
import 'firebase/compat/auth';
import { AnalyticsService } from '../analytics/analytics.service';
import { EnvironmentService } from '../env';
import { SpinnerService } from '../spinner';
import { UtilityService } from '../util';
import { InternalAuthState } from './internal-auth-state';
import { internalAuthStateEquality } from './internal-auth-state-equality';
import { getInternalAuthStateChangedHandler } from './get-internal-auth-state-changed-handler';
import { internalSignIn } from './internal-sign-in';


/**
 * A common authentication service used by internal EforAll apps.
 * The service will start automatically once the environment is available.
 */
@Injectable({ providedIn: 'root' })
export class InternalAuthService {

	private analytics = inject(AnalyticsService);
	private env = inject(EnvironmentService);
	private router = inject(Router);
	private util = inject(UtilityService);
	private spinnerService = inject(SpinnerService);


	public readonly state = signal<InternalAuthState>(
		{ status: 'pending' },
		{ equal: internalAuthStateEquality },
	);

	private loadData: (() => Promise<void>) | undefined = undefined;
	private clearData: (() => Promise<void>) | undefined = undefined;
	private loadDomainData: (() => Promise<void>) | undefined = undefined;
	private clearDomainData: (() => Promise<void>) | undefined = undefined;


	/**
	 * Firebase objects.
	 */
	public app = firebase.app();


	/**
	 * Service that wraps and monitors the Firebase auth state.
	 */
	constructor() {
		this.setState(this.state());
		this.start();
	}


	private async start() {

		//
		// Wait until the environment is available
		//
		const environment = await this.env.getEnvironmentPromise();

		const auth = this.app.auth();

		//
		// Listen for changes...
		//
		auth.onAuthStateChanged(
			getInternalAuthStateChangedHandler(
				auth,
				this.analytics,
				this.util,
				this.router,
				this.setState.bind(this),
				environment,
			),
			error => {
				this.analytics.logError(error);
				this.util.log.errorMessage(`${error.message}`);
			}
		);

	}


	public internalSignIn() {
		const environment = this.env.getEnvironment();
		internalSignIn(environment);
	}


	public signOut() {
		const auth = firebase.auth();
		auth.signOut();
	}


	public supportsLocalStorage(): boolean {
		let hasWebStorage = false;

		try {
			const test = 'test-for-support';
			localStorage.setItem(test, test)
			hasWebStorage = localStorage.getItem(test) === test;
			localStorage.removeItem(test);
		}
		catch (e) { hasWebStorage = false; }

		return hasWebStorage;
	}


	/**
	 * Get the firebase id token to send along with the payload to functions
	 */
	public async getIdToken(): Promise<string> {

		let attempt = 0;
		let auth = this.state();

		//
		// Try for 10 seconds  (100 * 100ms)
		// Consider: Should this just keep looping until signed in?
		//
		while (attempt++ < 100) {

			if (auth.status == 'signed-in') {
				return await auth.user.getIdToken();
			}

			await this.util.setTimeout(100);	// 100ms
			auth = this.state();
		}

		throw new Error(`getIdToken() failed because the auth state is ${auth.status}`);
	}


	/**
	 * Sets the state signal and triggers the spinner and data loading
	 */
	private setState(state: InternalAuthState) {
		this.state.set(state);
		const status = state.status;

		if (status == 'pending') {
			this.spinnerService.addSpinner('auth-pending', 'Signing In');
		}
		else {
			this.spinnerService.removeSpinner('auth-pending');
		}

		if (status == 'signed-in') {
			if (this.loadDomainData) this.loadDomainData();
			if (this.loadData) this.loadData();
		}
		else {
			if (this.clearDomainData) this.clearDomainData();
			if (this.clearData) this.clearData();
		}
	}

	
	/**
	 * @deprecated DO NOT USE THIS!  SHOULD BE IN THE APP-SPECIFIC APP SERVICE.
	 * 
	 * If provided, the auth service will call these methods
	 * to load or clear app data when the auth state changes.
	 * 
	 * @param loadData - A function to be called when the status is 'signed-in'
	 * @param clearData - A function to be called when the status is 'signed-out'
	 */
	public setData(
		loadData: () => Promise<void>,
		clearData: () => Promise<void>,
		loadDomainData?: () => Promise<void>,
		clearDomainData?: () => Promise<void>,
	) {
		this.loadDomainData = loadDomainData;
		this.clearDomainData = clearDomainData;
		this.loadData = loadData;
		this.clearData = clearData;
	}

}